import { useEffect, useRef } from 'react';

/* The phone's back button closes what is open on top instead of leaving the
   page. Each open sheet holds one extra history entry, marked so the entries of
   real pages are never touched. Changes settle in a microtask: a sheet replaced
   by another in one render (line sheet to box sheet) costs no history at all. */
const MARK = 'innySheet';
const stack = []; // { onClose, dismissible }, topmost last
let pushed = 0;
let ours = 0; // backs we asked for: their popstate is not the person's
let queued = false;
let listening = false;

const onTop = () => Boolean(window.history.state?.[MARK]);
const hold = () => window.history.pushState({ ...window.history.state, [MARK]: true }, '');

function settle() {
  queued = false;
  if (pushed && !onTop()) pushed = 0; // a page was opened from the sheet: its entries stay behind it
  while (pushed < stack.length) {
    hold();
    pushed += 1;
  }
  if (pushed > stack.length) {
    ours += 1;
    window.history.go(stack.length - pushed);
    pushed = stack.length;
  }
}
const schedule = () => {
  if (queued) return;
  queued = true;
  queueMicrotask(settle);
};

function onPop() {
  if (ours) {
    ours -= 1;
    return;
  }
  if (!pushed) return;
  pushed -= 1;
  const top = stack[stack.length - 1];
  if (!top) return;
  if (top.dismissible) top.onClose?.();
  else schedule(); // busy, or a result that must be read: stay
}

export function useBackClose(open, onClose, dismissible = true) {
  const entry = useRef({ onClose, dismissible });
  entry.current.onClose = onClose;
  entry.current.dismissible = dismissible;
  useEffect(() => {
    if (!open) return undefined;
    const me = entry.current;
    if (!listening) {
      listening = true; // for good: the answer to our own back comes after the sheet is gone
      window.addEventListener('popstate', onPop);
    }
    stack.push(me);
    schedule();
    return () => {
      stack.splice(stack.indexOf(me), 1);
      schedule();
    };
  }, [open]);
}
